import {
  Box,
  Button,
  Flex,
  Text,
  Image,
  useDisclosure,
  Link,
} from "@chakra-ui/react";
import { Avatar } from "../../user/Avatar";
import Autolink from "../../navigation/AutoLink";
import { ReactionsRow } from "../../reactions/ReactionsRows";
import { ReactionsPopover } from "../../reactions/ReactionsPopover";
import truncateString from "../../../utils/truncateString";
import { SealCheckThickIcon } from "../../icons/SealCheckThickIcon";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";
import { useIsMouseInput } from "../../../hooks/useIsMouseInput";
import { useUserData } from "../../../hooks/useUserData";
import { useRef, useState, useLayoutEffect, useEffect } from "react";
import { useTapHold } from "../../../hooks/useTapHold";
import { ReactionTypeEntity } from "../../../services/gc/types/reactionTypeEntity";
import { MessageReaction } from "../types";
import { useSlideInMenu } from "../../slideInMenu/hooks/useSlideInMenu";
import { useAccountData } from "../../../hooks/useAccountData";
import { FunnelContextArea } from "../../funnel/types";

interface ChatMessageCommentProps {
  username: string;
  date: string;
  text: string;
  isGold?: boolean;
  isGuest?: boolean;
  guestIcon?: string;
  avatar?: string;
  isOnline?: boolean;
  isAdmin?: boolean;
  reactions?: Record<string, MessageReaction[]>;
  handleReaction?: (data: ReactionTypeEntity) => void;
  handleDelete?: () => void;
  compact?: boolean;
}
export function ChatMessageContent({
  username,
  date,
  text,
  isGold,
  isGuest,
  guestIcon,
  avatar,
  isOnline,
  isAdmin,
  reactions,
  handleReaction,
  handleDelete,
  compact,
}: ChatMessageCommentProps) {
  const isSmall = useIsSmallBreakpoint();
  const isMouseInput = useIsMouseInput();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { userData } = useUserData();
  const { openUserProfile } = useSlideInMenu();
  const accountData = useAccountData();
  const isUserProfileEnabled = !!accountData?.features?.["user_profile"];

  const tapHoldProps = useTapHold({
    onHold: onOpen,
    onBlur: onClose,
    holdDuration: 750,
  });

  // Ref to measure the container for positioning and touch detection
  const containerRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState<"top" | "bottom">(
    "bottom",
  );

  // Update popover position based on available space
  useLayoutEffect(() => {
    const updatePopoverPosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < 100 && spaceAbove > spaceBelow) {
          setPopoverPosition("top");
        } else {
          setPopoverPosition("bottom");
        }
      }
    };

    if (isOpen) {
      updatePopoverPosition();
      window.addEventListener("resize", updatePopoverPosition);
      return () => window.removeEventListener("resize", updatePopoverPosition);
    }
  }, [isOpen]);

  // Listen for touchstart events on the document to detect touches outside the container.
  // This is used to close the popover when tapping outside the message because Safari does
  // not support onBlur callback.
  useEffect(() => {
    const handleTouchStart = (event: TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        tapHoldProps.onBlur();
      }
    };

    if (!isMouseInput) {
      document.addEventListener("touchstart", handleTouchStart);
      return () => document.removeEventListener("touchstart", handleTouchStart);
    }
  }, [isMouseInput, tapHoldProps]);

  return (
    <Box ref={containerRef} w="full">
      <Button
        aria-label={`${username}'s message at ${date}`}
        height="fit-content"
        position="relative"
        gap="12px"
        p="8px"
        justifyContent="flex-start"
        alignItems="flex-start"
        width="100%"
        _hover={{
          background: "transparent.white.05",
          borderRadius: "16px",
        }}
        _focus={
          isMouseInput
            ? {}
            : {
                background: isOpen ? "transparent.white.10" : "transparent",
              }
        }
        role="group"
        bg="transparent"
        lineHeight="normal"
        letterSpacing="normal"
        {...(isMouseInput || !userData ? {} : tapHoldProps)}
      >
        <Avatar
          size={compact ? "md" : "lg"}
          isGold={isGold}
          username={username}
          imageSrc={avatar}
          isOnline={isOnline}
          openProfileOnClick={isUserProfileEnabled}
          contextArea={FunnelContextArea.chat}
        />

        <Flex
          flexDirection="column"
          flexGrow={1}
          minWidth={0}
          gap={compact ? "8px" : "0"}
        >
          <Flex flexDirection={compact ? "row" : "column"} gap="8px">
            <Flex
              alignItems={compact ? "flex-start" : "center"}
              width={compact ? "fit-content" : "100%"}
              gap="8px"
            >
              <Link
                onClick={
                  username && isUserProfileEnabled
                    ? () => openUserProfile(username, { area: FunnelContextArea.chat })
                    : undefined
                }
              >
                <Flex gap="6px" alignItems="center">
                  <Text
                    fontWeight="600"
                    fontSize={compact ? "14px" : "16px"}
                    lineHeight={compact ? "20px" : "normal"}
                    color={
                      isGuest
                        ? "info.500"
                        : isAdmin
                          ? "blaze.blaze"
                          : "dune.dune"
                    }
                  >
                    {isSmall ? truncateString(username ?? "", 20) : username}
                  </Text>
                  {isGuest && guestIcon ? (
                    <Image src={guestIcon} height="20px" width="19px" />
                  ) : isAdmin ? (
                    <SealCheckThickIcon color="blaze.blaze" boxSize="16px" />
                  ) : null}
                </Flex>
              </Link>
              {compact ? null : (
                <Flex gap="8px">
                  <Text fontWeight="500" fontSize="12px" color="dune.700">
                    {date}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Text
              mb={compact ? "0px" : "8px"}
              mt={compact ? "0px" : "4px"}
              fontSize={compact ? "14px" : "15px"}
              color={compact ? "dune.700" : "dune.dune"}
              textAlign="left"
              fontWeight="400"
              lineHeight="150%"
              whiteSpace="pre-wrap"
            >
              <Autolink text={text} />
            </Text>
          </Flex>
          {reactions && handleReaction && (
            <ReactionsRow
              reactions={reactions}
              toggleReaction={handleReaction}
            />
          )}
        </Flex>

        {handleDelete && handleReaction && (
          <ReactionsPopover
            isOpen={isOpen}
            onToggle={onClose}
            deleteMessage={handleDelete}
            toggleReaction={handleReaction}
            position={popoverPosition}
          />
        )}
      </Button>
    </Box>
  );
}
